import type { BoardDocument, BoardVariantDocument, Frame, RenderMode, StudioBootstrap } from "@contracts";
import { BoardView } from "@runtime";
import Ruler from "@scena/react-ruler";
import Moveable, { type OnDrag, type OnDragEnd, type OnDragGroup, type OnDragStart, type OnResize, type OnResizeEnd, type OnResizeGroup } from "react-moveable";
import Selecto from "react-selecto";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { PreviewMode } from "./store";

interface CanvasEditorProps {
  bootstrap: StudioBootstrap;
  board: BoardDocument;
  variant: BoardVariantDocument;
  selectedIds: string[];
  previewMode: PreviewMode;
  fixtureState: string;
  onSelect: (ids: string[]) => void;
  onFrame: (id: string, patch: Partial<Frame>) => void;
  onFrames: (items: Array<{ id: string; patch: Partial<Frame> }>) => void;
  onDropPoint: (clientX: number, clientY: number) => { x: number; y: number } | null;
}

function grayTheme(bootstrap: StudioBootstrap, mode: PreviewMode) {
  if (mode === "design") return bootstrap.theme;
  return { ...bootstrap.theme, colors: { background: "#ffffff", foreground: "#111111", muted: "#777777", divider: "#111111" } };
}

export function CanvasEditor(props: CanvasEditorProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const boardWrapRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(.72);
  const [targets, setTargets] = useState<Array<HTMLElement | SVGElement>>([]);
  const dragOrigins = useRef(new Map<string, { frame: Frame; clientX: number; clientY: number }>());
  const resizeOrigins = useRef(new Map<string, Frame>());

  const updateScale = useCallback(() => {
    const container = viewportRef.current;
    if (!container) return;
    const availableWidth = Math.max(360, container.clientWidth - 44);
    const availableHeight = Math.max(300, container.clientHeight - 76);
    setScale(Math.min(1, Math.max(.25, Math.min(availableWidth / props.variant.canvas.width, availableHeight / props.variant.canvas.height))));
  }, [props.variant.canvas.height, props.variant.canvas.width]);

  useEffect(() => {
    updateScale();
    const observer = new ResizeObserver(updateScale);
    if (viewportRef.current) observer.observe(viewportRef.current);
    return () => observer.disconnect();
  }, [updateScale]);

  useEffect(() => {
    const root = boardWrapRef.current;
    setTargets(props.selectedIds.map((id) => root?.querySelector(`[data-instance-id="${CSS.escape(id)}"]`) as HTMLElement | null).filter(Boolean) as HTMLElement[]);
  }, [props.selectedIds, props.board, scale]);

  const context = useMemo(() => ({
    now: new Date(),
    theme: grayTheme(props.bootstrap, props.previewMode),
    modules: props.bootstrap.modules,
    snapshots: props.fixtureState === "live" ? props.bootstrap.snapshots : [],
    assets: props.bootstrap.assets,
    fixtureState: props.fixtureState === "live" ? undefined : props.fixtureState,
    previewStatus: props.fixtureState === "live" ? "live" as const : "fixture" as const,
  }), [props.bootstrap, props.fixtureState, props.previewMode]);

  const rememberDrag = (event: OnDragStart) => {
    dragOrigins.current = new Map(props.variant.instances.filter((item) => props.selectedIds.includes(item.id)).map((item) => [item.id, { frame: structuredClone(item.frame), clientX: event.clientX, clientY: event.clientY }]));
  };
  const rememberResize = () => {
    resizeOrigins.current = new Map(props.variant.instances.filter((item) => props.selectedIds.includes(item.id)).map((item) => [item.id, structuredClone(item.frame)]));
  };

  const dragOne = (event: OnDrag) => {
    const id = event.target.getAttribute("data-instance-id");
    const origin = id ? dragOrigins.current.get(id) : null;
    if (!id || !origin) return;
    const frame = origin.frame;
    const x = Math.max(0, Math.min(props.variant.canvas.width - frame.width, frame.x + (event.clientX - origin.clientX) / scale));
    const y = Math.max(0, Math.min(props.variant.canvas.height - frame.height, frame.y + (event.clientY - origin.clientY) / scale));
    event.target.style.left = `${x}px`;
    event.target.style.top = `${y}px`;
  };
  const dragGroup = (event: OnDragGroup) => event.events.forEach(dragOne);
  const commitDrag = () => {
    const patches = targets.map((target) => {
      const id = target.getAttribute("data-instance-id") ?? "";
      const origin = dragOrigins.current.get(id)?.frame;
      if (!origin) return null;
      return { id, patch: { x: Math.max(0, Math.min(props.variant.canvas.width - origin.width, Math.round(Number.parseFloat((target as HTMLElement).style.left)))), y: Math.max(0, Math.min(props.variant.canvas.height - origin.height, Math.round(Number.parseFloat((target as HTMLElement).style.top)))) } };
    }).filter(Boolean) as Array<{ id: string; patch: Partial<Frame> }>;
    if (patches.length) props.onFrames(patches);
  };

  const resizeOne = (event: OnResize) => {
    const id = event.target.getAttribute("data-instance-id");
    const origin = id ? resizeOrigins.current.get(id) : null;
    if (!id || !origin) return;
    const nextWidth = event.width / scale;
    const nextHeight = event.height / scale;
    event.target.style.width = `${nextWidth}px`;
    event.target.style.height = `${nextHeight}px`;
    if (event.drag) {
      event.target.style.left = `${origin.x + event.drag.beforeTranslate[0] / scale}px`;
      event.target.style.top = `${origin.y + event.drag.beforeTranslate[1] / scale}px`;
    }
  };
  const resizeGroup = (event: OnResizeGroup) => event.events.forEach(resizeOne);
  const commitResize = () => {
    const patches = targets.map((target) => {
      const id = target.getAttribute("data-instance-id") ?? "";
      const element = target as HTMLElement;
      return { id, patch: {
        x: Math.round(Number.parseFloat(element.style.left)),
        y: Math.round(Number.parseFloat(element.style.top)),
        width: Math.round(Number.parseFloat(element.style.width)),
        height: Math.round(Number.parseFloat(element.style.height)),
      } };
    });
    if (patches.length) props.onFrames(patches);
  };

  const targetFrames = props.variant.instances.filter((item) => props.selectedIds.includes(item.id));
  const selectedRangesX = targetFrames.map((item) => [item.frame.x, item.frame.x + item.frame.width]);
  const selectedRangesY = targetFrames.map((item) => [item.frame.y, item.frame.y + item.frame.height]);
  const designEnabled = props.previewMode === "design";

  return <main className={`canvas-editor preview-${props.previewMode}`} ref={viewportRef} onMouseDown={(event) => {
    if (event.target === event.currentTarget) props.onSelect([]);
  }}>
    <div className="canvas-stage" ref={stageRef} style={{ width: props.variant.canvas.width * scale + 34, height: props.variant.canvas.height * scale + 34 }}>
      <div className="ruler-corner"><span>{Math.round(scale * 100)}%</span></div>
      <div className="ruler-horizontal"><Ruler type="horizontal" width={props.variant.canvas.width * scale} height={34} zoom={scale} unit={100} segment={10} negativeRuler={false} range={[0, props.variant.canvas.width]} selectedRanges={selectedRangesX} backgroundColor="#f9fafb" lineColor="#b9bec4" textColor="#8a9097" selectedBackgroundColor="#cdeeed" /></div>
      <div className="ruler-vertical"><Ruler type="vertical" width={34} height={props.variant.canvas.height * scale} zoom={scale} unit={100} segment={10} negativeRuler={false} range={[0, props.variant.canvas.height]} selectedRanges={selectedRangesY} backgroundColor="#f9fafb" lineColor="#b9bec4" textColor="#8a9097" selectedBackgroundColor="#cdeeed" /></div>
      <div className="canvas-board-position" style={{ width: props.variant.canvas.width * scale, height: props.variant.canvas.height * scale }}>
        <div className="canvas-board-transform" ref={boardWrapRef} style={{ width: props.variant.canvas.width, height: props.variant.canvas.height, transform: `scale(${scale})` }}>
          <BoardView
            board={props.board}
            variant={props.variant}
            context={context}
            interactive={designEnabled}
            selectedIds={props.selectedIds}
            instanceClassName="sc-selectable"
            onSelect={(id, event) => {
              event.stopPropagation();
              const multi = event.shiftKey || event.metaKey;
              props.onSelect(multi ? (props.selectedIds.includes(id) ? props.selectedIds.filter((item) => item !== id) : [...props.selectedIds, id]) : [id]);
            }}
          />
        </div>
        {designEnabled && targets.length > 0 && <Moveable
          target={targets}
          container={stageRef.current ?? undefined}
          rootContainer={stageRef.current ?? undefined}
          zoom={1}
          draggable={targetFrames.every((item) => !item.frame.locked)}
          resizable={targetFrames.length === 1 && !targetFrames[0]?.frame.locked}
          snappable
          snapContainer={boardWrapRef.current ?? undefined}
          snapDirections={{ top: true, left: true, bottom: true, right: true, center: true, middle: true }}
          elementSnapDirections={{ top: true, left: true, bottom: true, right: true, center: true, middle: true }}
          horizontalGuidelines={[0, props.variant.canvas.height / 2, props.variant.canvas.height].map((value) => value * scale)}
          verticalGuidelines={[0, props.variant.canvas.width / 2, props.variant.canvas.width].map((value) => value * scale)}
          elementGuidelines={[...boardWrapRef.current?.querySelectorAll(".sc-instance") ?? []].filter((element) => !targets.includes(element as HTMLElement)) as HTMLElement[]}
          bounds={{ left: 0, top: 0, right: props.variant.canvas.width * scale, bottom: props.variant.canvas.height * scale, position: "css" }}
          onDragStart={rememberDrag}
          onDrag={dragOne}
          onDragGroupStart={rememberDrag}
          onDragGroup={dragGroup}
          onDragEnd={commitDrag as (event: OnDragEnd) => void}
          onDragGroupEnd={commitDrag as (event: OnDragEnd) => void}
          onResizeStart={(event) => { rememberResize(); const element = event.target as HTMLElement; event.set([element.offsetWidth, element.offsetHeight]); }}
          onResize={resizeOne}
          onResizeGroupStart={() => rememberResize()}
          onResizeGroup={resizeGroup}
          onResizeEnd={commitResize as (event: OnResizeEnd) => void}
          onResizeGroupEnd={commitResize as (event: OnResizeEnd) => void}
          keepRatio={false}
          throttleDrag={1}
          throttleResize={1}
        />}
        {designEnabled && <Selecto
          dragContainer={stageRef.current ?? undefined}
          selectableTargets={[".sc-selectable"]}
          selectByClick
          selectFromInside={false}
          continueSelect={false}
          toggleContinueSelect={["shift", "meta"]}
          hitRate={10}
          onDragStart={(event) => {
            if (targets.includes(event.inputEvent.target as HTMLElement) || (event.inputEvent.target as HTMLElement)?.closest(".moveable-control-box")) event.stop();
          }}
          onSelectEnd={(event) => props.onSelect(event.selected.map((element) => element.getAttribute("data-instance-id")).filter(Boolean) as string[])}
        />}
      </div>
    </div>
    {props.previewMode !== "design" && <div className="preview-mode-note">{props.previewMode === "gray" ? "灰度模拟 · 编辑已暂停" : "最终设备方向与后处理 · 编辑已暂停"}</div>}
  </main>;
}
